/**
 * This is an encapsulated mess.  It comes from the old discussion system and
 * does a lot of jQuery and DOM parsing to create the styling. This version
 * brings the code up to modern standards and removes the need to use jQuery
 *
 * It would be nice to make this better, but the data needs to come to us
 * structured, where now it comes as markup.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {formatContent} from "#corelibs-legacy-package/handlebars-extras.ts";

import type {Post} from "../types.ts";

type Props = {
    post: Post;
};

// Adapted from http://youmightnotneedjquery.com to replace $.parseHTML
// @ts-expect-error - TS2315 - Type 'HTMLCollection' is not generic.
function parseHTML(html: string): HTMLCollection<HTMLElement> {
    const doc = document.implementation.createHTMLDocument();
    if (!doc.body) {
        return new HTMLCollection();
    }

    doc.body.innerHTML = html;
    return doc.body.children || new HTMLCollection();
}

// Adapted from http://youmightnotneedjquery.com to replace $.filter
// on a NodeList.  (NodeLists from the DOM do not have .filter())
type NodePredicate = (node: HTMLElement) => boolean;
function nodeListFilter(
    // @ts-expect-error - TS2315 - Type 'HTMLCollection' is not generic.
    nodeList: HTMLCollection<HTMLElement>,
    predicate: NodePredicate,
): Array<HTMLElement> {
    return Array.prototype.filter.call(nodeList, predicate);
}

// @ts-expect-error - TS2315 - Type 'HTMLCollection' is not generic.
function getOverallInfoElement(parsed: HTMLCollection<HTMLElement>) {
    const overallTextEl = nodeListFilter(parsed, (item) =>
        item.classList.contains("overall-eval-info"),
    );
    const overallText =
        overallTextEl.length > 0 ? overallTextEl[0].textContent : "";

    // @ts-expect-error - TS2531 - Object is possibly 'null'.
    if (overallText.length === 0) {
        return null;
    }

    return (
        <div className={css(styles.moreInfo)}>
            <strong>{t`From the evaluator:`}</strong>
            <div
                dangerouslySetInnerHTML={{
                    __html: formatContent(overallText, true),
                }}
            />
        </div>
    );
}

function splitDetails(element: HTMLElement): {
    primary: string;
    extra: string;
} {
    // @ts-expect-error - TS2322 - Type 'Node' is not assignable to type 'HTMLElement'.
    element = element.cloneNode(true); // just to avoid mutating the input
    const div = element.querySelector("div");
    let extra = "";

    if (div) {
        // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string'.
        extra = div.textContent;
        div.innerHTML = "";
    }

    // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string'.
    return {primary: element.textContent, extra};
}

export default class DiscussionEvaluation extends React.Component<Props> {
    render(): React.ReactNode {
        const {post} = this.props;
        const {content, question} = post;

        const isPassingEvalAnswer = post.sumVotesIncremented > 1;

        const parsed = parseHTML(content);
        const items = nodeListFilter(parsed, (item) => {
            const isLI = item.tagName.toLowerCase() === "li";
            const passed = item.classList.contains("pass");
            return isLI && !passed;
        });

        const message = isPassingEvalAnswer
            ? t`This project has passed evaluation.`
            : t`This project needs more work.`;

        let contentView = null;

        if (isPassingEvalAnswer) {
            const overallInfo = getOverallInfoElement(parsed);
            // Include the extra details if there's overall info
            // specified on the review.
            if (overallInfo) {
                // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
                contentView = <div>{overallInfo}</div>;
            }
        } else {
            const failedObjectives = items.map(function (elem, i) {
                const {primary, extra} = splitDetails(elem);

                return (
                    <li key={"objective-" + i}>
                        <div className={css(styles.failedObjectiveItem)}>
                            <strong>{primary}</strong>
                        </div>
                        <div
                            className={css(styles.objectiveMoreInfo)}
                            dangerouslySetInnerHTML={{
                                __html: formatContent(extra, true),
                            }}
                        />
                    </li>
                );
            });

            // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
            contentView = (
                <div>
                    {getOverallInfoElement(parsed)}
                    <div className={css(styles.failedObjectives)}>
                        <strong>
                            {t`The following objectives need more work:`}
                        </strong>
                        <ul className={css(styles.styledList)}>
                            {failedObjectives}
                        </ul>
                    </div>
                </div>
            );
        }

        const headingContent = (question && question.content) || "";

        // TODO(BrianG): The `eval-guideline` style is still in `discussions.less`.
        // It has nested tag styles which can't be replicated in Aphrodite
        // Remove this link to discussion.less somehow
        return (
            <div>
                <div>{headingContent}</div>
                <div className="eval-guideline">
                    <div className={css(styles.evalTitle)}>{message}</div>
                    {contentView}
                </div>
            </div>
        );
    }
}

// Styles taken from discussion.less
const styles = StyleSheet.create({
    moreInfo: {
        marginTop: 20,
    },
    objectiveMoreInfo: {
        marginBottom: 10,
    },
    failedObjectiveItem: {
        marginTop: 10,
    },
    failedObjectives: {
        marginTop: 20,
    },
    styledList: {
        marginLeft: 16,
        listStyle: "disc",
    },
    evalTitle: {
        fontSize: "larger",
    },
});
